import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CONDITIONS, CURRENCIES } from "@/data/mock";
import LocationPicker from "@/components/common/LocationPicker";
import { countyOf } from "@/data/romania";
import { useToast } from "@/hooks/use-toast";
import { Upload, X, ImagePlus, Loader2 } from "lucide-react";
import { createListing, updateListing, uploadImage, fetchListing } from "@/lib/api";

export default function PostAd() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { categories, moderationEnabled, user } = useApp();
  const params = useParams();
  const editId = params.id || null;

  const [form, setForm] = useState({
    title: "", category: "", subcategory: "", condition: "Folosit",
    price: "", currency: "lei", county: "", city: "", locationDetails: "",
    description: "", sellerName: "", sellerPhone: "",
  });
  const [images, setImages] = useState([]); // [{url}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(!!editId);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        sellerName: f.sellerName || user.name || "",
        sellerPhone: f.sellerPhone || user.phone || "",
        county: f.county || user.county || "",
        city: f.city || user.city || "",
      }));
    }
  }, [user]);

  useEffect(() => {
    if (!editId) return;
    setLoading(true);
    fetchListing(editId).then((doc) => {
      const locDetail = doc.location && doc.location !== doc.city ? doc.location.replace(`${doc.city}, `, "") : "";
      setForm({
        title: doc.title, category: doc.category, subcategory: doc.subcategory || "",
        condition: doc.condition, price: String(doc.price), currency: doc.currency,
        county: doc.county || countyOf(doc.city), city: doc.city, locationDetails: locDetail,
        description: doc.description || "", sellerName: doc.seller_name || "", sellerPhone: doc.seller_phone || "",
      });
      setImages((doc.images || []).map((u) => ({ url: u })));
    }).catch(() => toast({ title: "Eroare", description: "Nu am putut încarcă anunțul." }))
      .finally(() => setLoading(false));
  }, [editId, toast]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cat = categories.find((c) => c.id === form.category);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - images.length);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        try {
          const res = await uploadImage(f);
          uploaded.push({ url: res.url });
        } catch (err) {
          toast({ title: "Eroare upload", description: err?.response?.data?.detail || "Nu am putut încarcă imaginea." });
        }
      }
      setImages((arr) => [...arr, ...uploaded].slice(0, 6));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const removeImg = (i) => setImages((arr) => arr.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.price || !form.city || !form.sellerName || !form.sellerPhone) {
      toast({ title: "Date incomplete", description: "Completează toate câmpurile obligatorii." });
      return;
    }
    if (images.length === 0) {
      toast({ title: "Lipsesc imaginile", description: "Adaugă cel puțin o imagine." });
      return;
    }
    setSubmitting(true);
    const payload = {
      title: form.title,
      category: form.category,
      subcategory: form.subcategory || "",
      condition: form.condition,
      price: Number(form.price),
      currency: form.currency,
      county: form.county,
      city: form.city,
      location: form.locationDetails ? `${form.city}, ${form.locationDetails}` : form.city,
      description: form.description,
      seller_name: form.sellerName,
      seller_phone: form.sellerPhone,
      images: images.map((i) => i.url),
    };
    try {
      let doc;
      if (editId) {
        doc = await updateListing(editId, payload);
        toast({ title: "Anunț actualizat", description: moderationEnabled ? "Anunțul revine în verificare." : "Modificările sunt vizibile." });
      } else {
        doc = await createListing(payload);
        toast({
          title: moderationEnabled ? "Trimis spre verificare" : "Anunț publicat",
          description: moderationEnabled ? "Anunțul va fi vizibil după aprobare." : "Anunțul tău este live.",
        });
      }
      setTimeout(() => navigate((moderationEnabled && !editId) ? "/profil" : `/anunt/${doc.id}`), 600);
    } catch (err) {
      toast({ title: "Eroare", description: err?.response?.data?.detail || "Nu am putut salva anunțul." });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="py-20 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin" style={{ color: "var(--amd-primary)" }} /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-10 fade-in">
      <h1 className="text-2xl md:text-3xl font-black text-white">{editId ? "Editează anunțul" : "Adaugă un anunț nou"}</h1>
      <p className="mt-1 text-sm" style={{ color: "var(--amd-text-muted)" }}>
        {moderationEnabled ? "Anunțurile noi sunt verificate manual înainte de publicare." : "Anunțurile sunt publicate instant."}
      </p>

      <form onSubmit={submit} className="mt-6 space-y-6">
        <Section title="Fotografii" hint={`${images.length}/6 imagini`}>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {images.map((img, i) => (
              <div key={i} className="relative aspect-square rounded-md overflow-hidden amd-card">
                <img src={img.url} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImg(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full grid place-items-center bg-black/70"><X className="w-3 h-3 text-white" /></button>
              </div>
            ))}
            {images.length < 6 && (
              <label className="aspect-square rounded-md amd-card cursor-pointer grid place-items-center text-center p-2">
                <div>
                  {uploading ? <Loader2 className="w-6 h-6 mx-auto animate-spin" style={{ color: "var(--amd-primary)" }} /> : <ImagePlus className="w-6 h-6 mx-auto" style={{ color: "var(--amd-primary)" }} />}
                  <div className="text-xs mt-1" style={{ color: "var(--amd-text-muted)" }}>{uploading ? "Se încarcă…" : "Adaugă foto"}</div>
                </div>
                <input type="file" accept="image/*" multiple onChange={onFiles} className="hidden" disabled={uploading} />
              </label>
            )}
          </div>
        </Section>

        <Section title="Detalii anunț">
          <div className="grid gap-4">
            <Field label="Titlu *">
              <Input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Ex: iPhone 14 Pro Max 256GB" className="amd-input h-11" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Categorie *">
                <Select value={form.category} onValueChange={(v) => { update("category", v); update("subcategory", ""); }}>
                  <SelectTrigger className="amd-input h-11"><SelectValue placeholder="Alege categoria" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Subcategorie">
                <Select value={form.subcategory || "none"} onValueChange={(v) => update("subcategory", v === "none" ? "" : v)} disabled={!cat}>
                  <SelectTrigger className="amd-input h-11"><SelectValue placeholder={cat ? "Alege" : "Alege întâi categoria"} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Fără subcategorie</SelectItem>
                    {(cat?.subcategories || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Stare">
                <Select value={form.condition} onValueChange={(v) => update("condition", v)}>
                  <SelectTrigger className="amd-input h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Preț *">
                <Input type="number" min="0" value={form.price} onChange={(e) => update("price", e.target.value)} placeholder="0" className="amd-input h-11" />
              </Field>
              <Field label="Monedă">
                <Select value={form.currency} onValueChange={(v) => update("currency", v)}>
                  <SelectTrigger className="amd-input h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Descriere">
              <Textarea rows={6} value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Descrie produsul: stare, accesorii, motivul vânzării…" className="amd-input" />
            </Field>
          </div>
        </Section>

        <Section title="Locație & Contact">
          <div className="grid gap-4">
            <Field label="Județ & Localitate *">
              <LocationPicker
                county={form.county}
                city={form.city}
                onChange={(county, city) => setForm((f) => ({ ...f, county, city }))}
                testIdPrefix="postad"
              />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Detalii locație">
                <Input value={form.locationDetails} onChange={(e) => update("locationDetails", e.target.value)} placeholder="Ex: Sector 3, Aviatorilor" className="amd-input h-11" />
              </Field>
              <Field label="Nume contact *">
                <Input value={form.sellerName} onChange={(e) => update("sellerName", e.target.value)} className="amd-input h-11" />
              </Field>
              <Field label="Telefon *">
                <Input value={form.sellerPhone} onChange={(e) => update("sellerPhone", e.target.value)} placeholder="07XX XXX XXX" className="amd-input h-11" />
              </Field>
            </div>
          </div>
        </Section>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} className="border-white/20 text-white hover:bg-white/5">Anulează</Button>
          <Button type="submit" disabled={submitting} className="amd-btn-primary h-11 px-6 gap-2">
            <Upload className="w-4 h-4" /> {submitting ? "Se trimite…" : editId ? "Salvează modificările" : "Publică anunțul"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div className="amd-card rounded-lg p-4 md:p-5">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-bold text-white">{title}</h2>
        {hint && <span className="text-xs" style={{ color: "var(--amd-text-muted)" }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-white text-xs uppercase tracking-wider">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
